import { AllocationRule } from 'further-types/lp/allocations';
import { TransactionOperation } from 'further-types/lp/transaction';
import { round, sumBy } from 'lodash';
import { z } from 'zod';
import { CalendarDay } from '../../../utils';
import { isMongoId } from '../../helpers';
import { allocationMatchesCheckAmount } from '../../helpers/transaction-allocations-input';
import { CreateAllocation } from '../allocations';

const ManualActionSchema = z.object({
  ledgerDate: z.instanceof(CalendarDay, {
    message: 'Please select a ledger date.',
  }),
  counterparty: z.object(
    {
      id: z
        .string()
        .optional()
        .refine(isMongoId({ optional: true })),
      name: z.string().min(1, {
        message: 'Please enter a counterparty name.',
      }),
    },
    {
      message: 'Please enter a counterparty name.',
    },
  ),
  transactions: z
    .array(
      z.object({
        id: z
          .string()
          .refine(isMongoId({ optional: true }))
          .optional(),
        reconcilableTransactionId: z
          .string()
          .refine(isMongoId({ optional: true }), {
            message: 'Please select a valid transaction to reconcile.',
          })
          .optional(),
        transactionTypeId: z.string().refine(isMongoId(), {
          message: 'Please select a transaction type.',
        }),
        amountLcy: z.number().min(0.01),
        amountTcy: z.number().min(0.01),
        operationType: z.nativeEnum(TransactionOperation),
      }),
    )
    .min(2, {
      message: 'Please enter at least two transactions.',
    }),
  description: z.string().min(1, {
    message: 'Please enter a description.',
  }),
  allocationRule: z.nativeEnum(AllocationRule, {
    message: 'Please select an allocation rule.',
  }),
  allocations: z.array(CreateAllocation, {
    message: 'Please allocate the value.',
  }),
  totalAmountLcy: z.number().min(0.01, {
    message: 'Please enter a total amount.',
  }),
  totalAmountTcy: z.number().min(0.01, {
    message: 'Please enter a total amount.',
  }),
  isInMutualReconciliationMode: z.boolean().optional(),
});

export const CreateManualAction = ManualActionSchema.refine(
  (data) =>
    !!data.isInMutualReconciliationMode ||
    allocationMatchesCheckAmount(data.totalAmountTcy, data.allocations),
  {
    message: 'Total allocation amount must match input amount.',
    path: ['allocations'],
  },
).refine(
  (data) =>
    round(
      sumBy(
        data.transactions.filter(
          (t) => t.operationType === TransactionOperation.Debit,
        ),
        (transaction) => transaction.amountTcy,
      ),
      2,
    ) ===
    round(
      sumBy(
        data.transactions.filter(
          (t) => t.operationType === TransactionOperation.Credit,
        ),
        (transaction) => transaction.amountTcy,
      ),
      2,
    ),
  {
    message: 'Credit and debit amounts must match.',
    path: ['transactions'],
  },
);

export const UpdateManualAction = ManualActionSchema.extend({
  id: z.string().refine(isMongoId()),
})
  .refine(
    (data) =>
      !!data.isInMutualReconciliationMode ||
      allocationMatchesCheckAmount(data.totalAmountTcy, data.allocations),
    {
      message: 'Total allocation amount must match input amount.',
      path: ['allocations'],
    },
  )
  .refine(
    (data) =>
      round(
        sumBy(
          data.transactions.filter(
            (t) => t.operationType === TransactionOperation.Debit,
          ),
          (transaction) => transaction.amountTcy,
        ),
        2,
      ) ===
      round(
        sumBy(
          data.transactions.filter(
            (t) => t.operationType === TransactionOperation.Credit,
          ),
          (transaction) => transaction.amountTcy,
        ),
        2,
      ),
    {
      message: 'Credit and debit amounts must match.',
      path: ['transactions'],
    },
  );
